/**
 * @fileoverview Cookie Consent Banner component.
 * @summary RGPD-compliant cookie consent banner with persistent storage.
 *
 * @package KaraoNow
 * @module apps/front/src/components/CookieBanner
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'

/**
 * @summary Cookie Consent Banner component.
 * @description Shows a RGPD-compliant cookie consent banner that persists in localStorage.
 * @returns {React.ReactElement | null} Cookie banner or null if consent already given.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function CookieBanner() {
  const { t } = useTranslation()
  const [showBanner, setShowBanner] = useState(false)

  useEffect(() => {
    // Check if user has already dismissed the banner
    const cookieBannerDismissed = localStorage.getItem('cookieBannerDismissed')
    if (!cookieBannerDismissed) {
      setShowBanner(true)
    }
  }, [])

  const handleDismiss = () => {
    localStorage.setItem('cookieBannerDismissed', 'true')
    localStorage.setItem('cookieBannerDismissedDate', new Date().toISOString())
    setShowBanner(false)
  }

  if (!showBanner) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto bg-amber-100 dark:bg-gray-800 border border-amber-500 rounded-lg shadow-2xl  p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <h3 className="text-base sm:text-lg font-semibold text-(--color-text-primary)">
            {t('cookies.title')}
          </h3>
          <button
            onClick={handleDismiss}
            className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors shrink-0"
            aria-label={t('common.close')}
          >
            <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-(--color-text-secondary) mb-4">
          {t('cookies.description')}
          {' '}
          <a
            href="/cookies"
            className="text-primary-600 dark:text-primary-400 hover:underline font-semibold"
          >
            {t('cookies.learnMore')}
          </a>
        </p>

        {/* Action */}
        <div className="flex gap-3">
          <button
            onClick={handleDismiss}
            className="btn btn-primary flex-1 text-center"
          >
            {t('cookies.understood')}
          </button>
        </div>

        {/* Footer note */}
        {/* <p className="text-xs text-(--color-text-secondary) mt-3">
          {t('cookies.footerNote')}
        </p> */}
      </div>
    </div>
  )
}

